package com.evoapps.evoshift.domain.usecase.shift

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.domain.model.AuditMetadata
import com.evoapps.evoshift.domain.model.DailyRecordIds
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.ShiftTemplate
import com.evoapps.evoshift.domain.util.plannedTimeRange
import kotlinx.datetime.LocalDate

/** The single place a new shift document is assembled (skeleton, roster builder, templates). */
object ShiftFactory {
    /**
     * Older shifts were stamped with this role when their template had none; it is read back as
     * "no role" (ShiftMapper). No role means anyone may work the shift.
     */
    const val LEGACY_DEFAULT_ROLE = "Employee"

    fun newShift(
        rosterId: String,
        date: LocalDate,
        startTime: String,
        endTime: String,
        role: String?,
        skills: List<String> = emptyList(),
        createdBy: String,
        userId: String? = null,
        userDisplayName: String? = null,
        now: String = AppClock.now().toString()
    ): Shift = Shift(
        id = "", // assigned by the repository
        rosterId = rosterId,
        date = date.toString(),
        dailyRecordId = DailyRecordIds.of(RosterIds.workplaceIdOf(rosterId), date.toString()),
        requiredRole = role?.takeIf { it.isNotBlank() },
        requiredSkills = skills,
        userId = userId,
        userDisplayName = userDisplayName,
        actualUserId = null,
        isPublished = false,
        hasUnpublishedChanges = true,
        version = 1,
        plannedTime = plannedTimeRange(date, startTime, endTime),
        actualTime = null,
        swapRequest = null,
        conflictMetadata = null,
        metadata = AuditMetadata(createdAt = now, createdBy = createdBy),
        absenceId = null,
        tags = emptyList()
    )

    fun fromTemplate(
        rosterId: String,
        date: LocalDate,
        template: ShiftTemplate,
        createdBy: String,
        userId: String? = null,
        userDisplayName: String? = null
    ): Shift = newShift(
        rosterId = rosterId, date = date, startTime = template.startTime, endTime = template.endTime,
        role = template.role, skills = template.skills, createdBy = createdBy,
        userId = userId, userDisplayName = userDisplayName
    )
}
